import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Login from "./Login";
import socket from "./socket";
import API, { resetSessionExpiredGuard } from "./api";
import useDialogFocusTrap from "./useDialogFocusTrap";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { getPrimaryRoleName, hasPermission } from "./rbac";
import {
  getActiveStoredAccount,
  getStoredAccounts,
  getStoredActiveAccountId,
  clearBrowserStorageData,
  removeStoredAccount,
  setStoredActiveAccount,
  updateStoredAccountUser,
  upsertStoredAccount,
} from "./accountSession";
import { clearAllCreateProfileDraftsForUser } from "./createProfileDraftStorage";

const DashboardHome = lazy(() => import("./DashboardHome"));
const Search = lazy(() => import("./Search"));
const AssignedProfiles = lazy(() => import("./AssignedProfiles"));
const CreateProfile = lazy(() => import("./CreateProfile"));
const ProfileDetail = lazy(() => import("./Profiledetail"));
const AuditLogs = lazy(() => import("./AuditLogs"));
const UserManagement = lazy(() => import("./UserManagement"));
const PermissionDocs = lazy(() => import("./PermissionDocs"));
const DuplicateMergeHistory = lazy(() => import("./DuplicateMergeHistory"));
const RecommendationDashboard = lazy(() => import("./RecommendationDashboard"));
const HrmsAnalytics = lazy(() => import("./HrmsAnalytics"));
const TaskAssignment = lazy(() => import("./TaskAssignment"));
const MyProfile = lazy(() => import("./MyProfile"));
const FutureProspects = lazy(() => import("./FutureProspects"));
const ProfileAlerts = lazy(() => import("./ProfileAlerts"));
const ImportExport = lazy(() => import("./ImportExport"));
const WatermarkedPdfRedactionPage = lazy(() => import("./WatermarkedPdfRedactionPage"));
const TempRecommendationImport = lazy(() => import("./TempRecommendationImport"));
const YashFe = lazy(() => import("./yashfe"));

const PROFILE_CREATE_FIELD_KEYS = [
  "duplicate_check",
  "basic_details",
  "community",
  "education_career",
  "addresses",
  "contacts",
  "family_relatives",
  "preferences",
  "persona",
  "payments",
  "tags",
  "photos",
  "documents",
  "primary_photo",
  "phone",
  "email",
  "family_details",
];

const BRAND = {
  // red/mix/black stay fixed brand-mark colors (used only in the logo/avatar
  // gradients) - see LOGO_GRADIENT below for the dark-mode-aware variant.
  red: "#D73C2E",
  black: "#14141a",
  mix: "#B9343A",
  redSoft: "var(--shell-red-soft)",
  redSoftAlt: "var(--shell-red-soft)",
  border: "var(--shell-border)",
  borderStrong: "var(--shell-border-strong)",
  text: "var(--shell-ink)",
  muted: "var(--shell-muted)",
  whiteGlass: "var(--shell-white-glass)",
};

// Logo/avatar gradient stops: unlike BRAND.red/mix/black (kept fixed above),
// this variant brightens for dark mode so the mark stays visible against a
// near-black sidebar instead of fading into it.
const LOGO_GRADIENT_3 = "linear-gradient(135deg, var(--shell-logo-1) 0%, var(--shell-logo-2) 58%, var(--shell-logo-3) 100%)";
const LOGO_GRADIENT_2 = "linear-gradient(135deg, var(--shell-logo-1) 0%, var(--shell-logo-2) 100%)";

const GOOGLE_CLIENT_ID =
  "956333367669-8g9nrjrdakbshlhtrhibmve21ih1e5im.apps.googleusercontent.com";

const DEFAULT_APP_ROUTE = {
  view: "search",
  detailReturnView: "search",
  selectedProfileId: null,
  selectedProfileMode: "view",
  selectedProfileInitialTab: null,
  selectedProfileEditSection: null,
  selectedProfileLockedSection: null,
  selectedProfileAlertType: "all",
  userManagementTab: "users",
  userManagementProfileId: null,
  createBiodataItemId: null,
  taskAssignmentTab: null,
  taskAssignmentWorkspaceTab: null,
  importExportBatchId: null,
};

const APP_VIEWS = new Set([
  "dashboard",
  "search",
  "create",
  "detail",
  "profile-alerts",
  "assigned-profiles",
  "recommendation-dashboard",
  "future-prospects",
  "hrms-analytics",
  "task-assignment",
  "import-workspace",
  "export-workspace",
  "temp-recommendation-import",
  "my-profile",
  "audit",
  "duplicates",
  "users",
  "permission-docs",
  "watermarked-pdf-redaction",
  "yashfe",
]);

const USER_MANAGEMENT_TABS = new Set([
  "dropdowns",
  "users",
  "roles",
  "features",
  "permissions",
  "user_types",
  "profile_verification",
  "policies",
  "diagnostics",
]);

const PROFILE_ALERT_TYPES = new Set(["all", "payment", "follow_up", "profile"]);
const TASK_ASSIGNMENT_TABS = new Set(["assigned-to-me", "assigned-by-me", "team-tasks"]);
const TASK_ASSIGNMENT_WORKSPACE_TABS = new Set(["tasks", "profiles", "biodata"]);

function normalizeUserManagementTab(value) {
  const tab = String(value || "").trim();
  return USER_MANAGEMENT_TABS.has(tab) ? tab : "";
}

function normalizeProfileAlertType(value) {
  const alertType = String(value || "").trim();
  return PROFILE_ALERT_TYPES.has(alertType) ? alertType : "";
}

function normalizeTaskAssignmentTab(value) {
  const tab = String(value || "").trim();
  return TASK_ASSIGNMENT_TABS.has(tab) ? tab : "";
}

function normalizeTaskAssignmentWorkspaceTab(value) {
  const tab = String(value || "").trim();
  return TASK_ASSIGNMENT_WORKSPACE_TABS.has(tab) ? tab : "";
}

function normalizeProfileSection(value) {
  const section = String(value || "").trim().toLowerCase();
  return section || null;
}

function parseAppRouteHash(hashValue = "") {
  if (typeof window === "undefined") return { ...DEFAULT_APP_ROUTE };

  const rawHash = String(hashValue || "").replace(/^#\/?/, "").trim();
  if (!rawHash) return { ...DEFAULT_APP_ROUTE };

  const [rawPath, rawQuery = ""] = rawHash.split("?");
  const segments = rawPath.split("/").map((part) => decodeURIComponent(part || "").trim()).filter(Boolean);
  const params = new URLSearchParams(rawQuery);
  const route = { ...DEFAULT_APP_ROUTE };

  if (segments[0] === "profiles" || segments[0] === "profile" || segments[0] === "detail") {
    const profileId = segments[1] || params.get("id") || null;
    if (!profileId) return { ...DEFAULT_APP_ROUTE };
    route.view = "detail";
    route.selectedProfileId = profileId;
    route.selectedProfileMode = segments[2] === "edit" ? "edit" : "view";
    route.selectedProfileInitialTab = params.get("tab") || null;
    route.selectedProfileEditSection = normalizeProfileSection(params.get("editSection"));
    route.selectedProfileLockedSection = normalizeProfileSection(params.get("lockedSection"));
    route.detailReturnView = normalizeAppView(params.get("from")) || "search";
    return route;
  }

  if (segments[0] === "import-export") {
    const legacyTab = String(params.get("importExportTab") || "").trim().toLowerCase();
    route.view = legacyTab === "export" ? "export-workspace" : "import-workspace";
    route.importExportBatchId = params.get("importBatch") || null;
    route.detailReturnView = route.view;
    return route;
  }

  route.view = normalizeAppView(segments[0]) || "search";
  route.selectedProfileInitialTab = params.get("tab") || null;
  route.selectedProfileAlertType = normalizeProfileAlertType(params.get("alertType")) || "all";
  route.userManagementTab = normalizeUserManagementTab(params.get("usersTab")) || "users";
  route.userManagementProfileId = params.get("verificationProfile") || null;
  if (route.userManagementProfileId) {
    route.userManagementTab = "profile_verification";
  }
  route.createBiodataItemId = params.get("biodataItem") || null;
  route.taskAssignmentTab = normalizeTaskAssignmentTab(params.get("taskTab")) || null;
  route.taskAssignmentWorkspaceTab = normalizeTaskAssignmentWorkspaceTab(params.get("workspaceTab")) || null;
  route.importExportBatchId = params.get("importBatch") || null;
  route.detailReturnView = normalizeAppView(params.get("from")) || route.view || "search";
  return route;
}

function normalizeAppView(value) {
  const view = String(value || "").trim();
  return APP_VIEWS.has(view) ? view : "";
}

function buildAppRouteHash({
  view,
  detailReturnView,
  selectedProfileId,
  selectedProfileMode,
  selectedProfileInitialTab,
  selectedProfileEditSection,
  selectedProfileLockedSection,
  selectedProfileAlertType,
  userManagementTab,
  userManagementProfileId,
  createBiodataItemId,
  taskAssignmentTab,
  taskAssignmentWorkspaceTab,
  importExportBatchId,
}) {
  const cleanView = normalizeAppView(view) || "search";
  const params = new URLSearchParams();

  if (cleanView === "detail" && selectedProfileId) {
    if (selectedProfileInitialTab) params.set("tab", selectedProfileInitialTab);
    if (selectedProfileEditSection) params.set("editSection", selectedProfileEditSection);
    if (selectedProfileLockedSection) params.set("lockedSection", selectedProfileLockedSection);
    if (detailReturnView && detailReturnView !== "search") params.set("from", detailReturnView);
    const modePath = selectedProfileMode === "edit" ? "/edit" : "";
    const query = params.toString();
    return `#/profiles/${encodeURIComponent(selectedProfileId)}${modePath}${query ? `?${query}` : ""}`;
  }

  if (cleanView === "detail") return "#/search";

  if (cleanView === "profile-alerts" && selectedProfileAlertType && selectedProfileAlertType !== "all") {
    params.set("alertType", selectedProfileAlertType);
  }

  if (cleanView === "users" && userManagementTab && userManagementTab !== "users") {
    params.set("usersTab", userManagementTab);
  }

  if (cleanView === "users" && userManagementTab === "profile_verification" && userManagementProfileId) {
    params.set("verificationProfile", userManagementProfileId);
  }

  if (cleanView === "create" && createBiodataItemId) {
    params.set("biodataItem", createBiodataItemId);
  }

  if (cleanView === "task-assignment") {
    if (taskAssignmentTab) params.set("taskTab", taskAssignmentTab);
    if (taskAssignmentWorkspaceTab) params.set("workspaceTab", taskAssignmentWorkspaceTab);
  }

  if (cleanView === "import-workspace" && importExportBatchId) {
    params.set("importBatch", importExportBatchId);
  }

  const query = params.toString();
  return `#/${cleanView}${query ? `?${query}` : ""}`;
}

function syncAppRouteHash(nextHash, { replace = true } = {}) {
  if (typeof window === "undefined" || !nextHash) return;
  const nextUrl = `${window.location.pathname}${window.location.search}${nextHash}`;
  if (`${window.location.hash || ""}` === nextHash) return;
  if (replace) {
    window.history.replaceState(null, "", nextUrl);
    return;
  }
  window.history.pushState(null, "", nextUrl);
}

function parseInitialAppRoute() {
  if (typeof window === "undefined") return { ...DEFAULT_APP_ROUTE };
  if (window.location.hash) return parseAppRouteHash(window.location.hash);

  const pathRoute = String(window.location.pathname || "")
    .replace(/^\/+|\/+$/g, "")
    .trim();
  if (!pathRoute) return { ...DEFAULT_APP_ROUTE };

  return parseAppRouteHash(`#/${pathRoute}${window.location.search || ""}`);
}

export default function App() {
  const initialRoute = useMemo(() => parseInitialAppRoute(), []);
  const initialAccounts = useMemo(() => getStoredAccounts(), []);
  const initialActiveAccount = useMemo(
    () => getActiveStoredAccount(initialAccounts, getStoredActiveAccountId()),
    [initialAccounts]
  );
  const [accounts, setAccounts] = useState(initialAccounts);
  const [activeAccountId, setActiveAccountId] = useState(initialActiveAccount?.id || null);
  const [view, setView] = useState(initialRoute.view);
  const [detailReturnView, setDetailReturnView] = useState(initialRoute.detailReturnView);
  const [selectedProfileId, setSelectedProfileId] = useState(initialRoute.selectedProfileId);
  const [selectedProfileMode, setSelectedProfileMode] = useState(initialRoute.selectedProfileMode);
  const [selectedProfileInitialTab, setSelectedProfileInitialTab] = useState(initialRoute.selectedProfileInitialTab);
  const [selectedProfileEditSection, setSelectedProfileEditSection] = useState(initialRoute.selectedProfileEditSection);
  const [selectedProfileLockedSection, setSelectedProfileLockedSection] = useState(initialRoute.selectedProfileLockedSection);
  const [selectedProfileAlertType, setSelectedProfileAlertType] = useState(initialRoute.selectedProfileAlertType);
  // Where to send the user back to when they click "Back to Overlay" on the
  // profile they just navigated to from a recommendation overlay row.
  const [profileOverlayReturn, setProfileOverlayReturn] = useState(null);
  // A one-shot descriptor of which recommendation overlay to reopen on the
  // profile we're navigating to (set when following a "Back to Overlay" click).
  const [profileReopenOverlay, setProfileReopenOverlay] = useState(null);
  const [userManagementTab, setUserManagementTab] = useState(initialRoute.userManagementTab);
  const [userManagementProfileId, setUserManagementProfileId] = useState(initialRoute.userManagementProfileId);
  const [createBiodataItemId, setCreateBiodataItemId] = useState(initialRoute.createBiodataItemId);
  const [taskAssignmentTab, setTaskAssignmentTab] = useState(initialRoute.taskAssignmentTab);
  const [taskAssignmentWorkspaceTab, setTaskAssignmentWorkspaceTab] = useState(initialRoute.taskAssignmentWorkspaceTab);
  const [importExportBatchId, setImportExportBatchId] = useState(initialRoute.importExportBatchId);
  const [createProfileFreshStartKey] = useState(0);
  const [sessionMsg, setSessionMsg] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("jk-sidebar-collapsed") === "1";
  });
  const [sidebarSectionsExpanded, setSidebarSectionsExpanded] = useState({ core: true, admin: true });
  const [darkModeOn, setDarkModeOn] = useState(() => {
    if (typeof window === "undefined") return false;
    // Default is always light: dark only when the user explicitly chose it before.
    return window.localStorage.getItem("jk-theme") === "dark";
  });
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-theme", darkModeOn ? "dark" : "light");
    try {
      window.localStorage.setItem("jk-theme", darkModeOn ? "dark" : "light");
    } catch (_err) {
      /* localStorage unavailable (private mode, quota) - theme just won't persist */
    }
  }, [darkModeOn]);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [navDropdownOpen, setNavDropdownOpen] = useState("");
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const sessionExpiredModalTrap = useDialogFocusTrap(Boolean(sessionMsg));
  const addAccountModalTrap = useDialogFocusTrap(addAccountOpen);
  const logoutConfirmModalTrap = useDialogFocusTrap(logoutConfirmOpen);
  const [roleSwitching, setRoleSwitching] = useState(false);
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const [authHydrating, setAuthHydrating] = useState(true);
  const accountMenuRef = useRef(null);
  const workMenuRef = useRef(null);
  const importExportMenuRef = useRef(null);
  const reportsMenuRef = useRef(null);
  const routeHistoryModeRef = useRef("replace");
  const refreshCurrentUserPromiseRef = useRef(null);
  const lastUserRefreshAtRef = useRef(0);
  const activeAccount = useMemo(
    () => accounts.find((account) => account.id === activeAccountId) || accounts[0] || null,
    [accounts, activeAccountId]
  );
  const user = activeAccount?.user || null;
  const token = "";
  const isAuthenticated = Boolean(user?.id);

  // ─── Role helpers for new schema ─────────────────────────────
  // New backend may return:
  // user.role_name
  // user.roles = [{ id, name }]
  // user.role_id
  const role = useMemo(() => getPrimaryRoleName(user), [user]);
  const activeRoleId = user?.effective_role_id || user?.active_role_id || user?.role_id || null;
  const switchableRoles = useMemo(
    () => (Array.isArray(user?.roles) ? user.roles : []),
    [user?.roles]
  );
  const currentRole = useMemo(
    () =>
      switchableRoles.find((candidate) => Number(candidate?.id) === Number(activeRoleId || 0)) ||
      switchableRoles[0] ||
      null,
    [switchableRoles, activeRoleId]
  );
  const alternateRoles = useMemo(
    () => switchableRoles.filter((candidate) => Number(candidate?.id) !== Number(activeRoleId || 0)),
    [switchableRoles, activeRoleId]
  );
  const userTypeLabel = user?.user_type_name || user?.user_type?.name || user?.user_type_code || null;
  const canDashboard = hasPermission(user, "dashboard", "view");
  const canSearch = hasPermission(user, "profiles", "search");
  const canViewProfile = hasPermission(user, "profiles", "view");
  const canViewAlerts = hasPermission(user, "profiles", "view", "alerts");
  const canCreate =
    hasPermission(user, "profiles", "create") ||
    PROFILE_CREATE_FIELD_KEYS.some((fieldKey) => hasPermission(user, "profiles", "create", fieldKey));
  const canAudit = hasPermission(user, "audit", "view");
  const canReports = hasPermission(user, "reports", "view");
  const canImport = hasPermission(user, "imports", "create");
  // exports:create also still gates Temp Recommendation Import.
  const canExport = hasPermission(user, "exports", "create");
  const canWatermarkRedaction = hasPermission(user, "profiles", "download");
  const canHrmsAnalytics = hasPermission(user, "reports", "view", "hrms_analytics");
  const canHrmsAnalyticsRebuild = hasPermission(user, "reports", "rebuild", "hrms_analytics");
  const canBiodataAssignmentsAccess =
    hasPermission(user, "biodata_assignments", "view") ||
    hasPermission(user, "biodata_assignments", "assign") ||
    hasPermission(user, "biodata_assignments", "update") ||
    hasPermission(user, "biodata_assignments", "complete");
  const canTaskAssignments =
    canBiodataAssignmentsAccess ||
    hasPermission(user, "profiles", "assign") ||
    hasPermission(user, "tasks", "view") ||
    hasPermission(user, "tasks", "create") ||
    hasPermission(user, "tasks", "update") ||
    hasPermission(user, "tasks", "complete") ||
    hasPermission(user, "tasks", "reassign");
  const canFutureProspects = hasPermission(user, "future_prospects", "view");
  const canUpdateFutureProspects = hasPermission(user, "future_prospects", "update");
  const canUsers = Boolean(user?.isSuperAdmin);
  const canDuplicateHistory = hasPermission(user, "duplicates", "view");
  const canResolveDuplicates = hasPermission(user, "duplicates", "resolve");
  const keepCreateMounted = canCreate && (view === "create" || (view === "detail" && detailReturnView === "create"));
  const keepFutureProspectsMounted =
    canFutureProspects && (view === "future-prospects" || (view === "detail" && detailReturnView === "future-prospects"));
  const routeHash = useMemo(
    () =>
      buildAppRouteHash({
        view,
        detailReturnView,
        selectedProfileId,
        selectedProfileMode,
        selectedProfileInitialTab,
        selectedProfileEditSection,
        selectedProfileLockedSection,
        selectedProfileAlertType,
        userManagementTab,
        userManagementProfileId,
        createBiodataItemId,
        taskAssignmentTab,
        taskAssignmentWorkspaceTab,
        importExportBatchId,
      }),
    [
      view,
      detailReturnView,
      selectedProfileId,
      selectedProfileMode,
      selectedProfileInitialTab,
      selectedProfileEditSection,
      selectedProfileLockedSection,
      selectedProfileAlertType,
      userManagementTab,
      userManagementProfileId,
      createBiodataItemId,
      taskAssignmentTab,
      taskAssignmentWorkspaceTab,
      importExportBatchId,
    ]
  );

  const clearLocalAuthState = useCallback(async ({ revokeServer = true } = {}) => {
    if (revokeServer) {
      try {
        await API.post("/auth/clear-client-session", {}, { skipSessionExpired: true });
      } catch (error) {
        // The browser storage cleanup below is still the important local guarantee.
      }
    }

    const result = await clearBrowserStorageData();
    setAccounts(result.accounts);
    setActiveAccountId(result.activeAccountId);
    resetSessionExpiredGuard();
    return result;
  }, []);

  const clearActiveAccountState = useCallback(async ({ revokeServer = false } = {}) => {
    const result = removeStoredAccount(activeAccountId || activeAccount?.id || user?.id);
    setAccounts(result.accounts);
    setActiveAccountId(result.activeAccountId);
    resetSessionExpiredGuard();

    if (!result.accounts.length) {
      await clearLocalAuthState({ revokeServer });
    }

    return result;
  }, [activeAccountId, activeAccount?.id, user?.id, clearLocalAuthState]);

  useEffect(() => {
    if (!isAuthenticated) return;
    syncAppRouteHash(routeHash, { replace: routeHistoryModeRef.current !== "push" });
    routeHistoryModeRef.current = "replace";
  }, [isAuthenticated, routeHash]);

  useEffect(() => {
    const handleRouteChange = () => {
      const nextRoute = parseAppRouteHash(window.location.hash);
      setView(nextRoute.view);
      setDetailReturnView(nextRoute.detailReturnView);
      setSelectedProfileId(nextRoute.selectedProfileId);
      setSelectedProfileMode(nextRoute.selectedProfileMode);
      setSelectedProfileInitialTab(nextRoute.selectedProfileInitialTab);
      setSelectedProfileEditSection(nextRoute.selectedProfileEditSection);
      setSelectedProfileLockedSection(nextRoute.selectedProfileLockedSection);
      setSelectedProfileAlertType(nextRoute.selectedProfileAlertType);
      setUserManagementTab(nextRoute.userManagementTab);
      setUserManagementProfileId(nextRoute.userManagementProfileId);
      setCreateBiodataItemId(nextRoute.createBiodataItemId);
      setTaskAssignmentTab(nextRoute.taskAssignmentTab);
      setTaskAssignmentWorkspaceTab(nextRoute.taskAssignmentWorkspaceTab);
      setImportExportBatchId(nextRoute.importExportBatchId);
      setMobileOpen(false);
      setAccountMenuOpen(false);
      setRoleMenuOpen(false);
      setNavDropdownOpen("");
    };

    window.addEventListener("hashchange", handleRouteChange);
    return () => window.removeEventListener("hashchange", handleRouteChange);
  }, []);

  // ─── Session expired listener ────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      void clearActiveAccountState();
      setSessionMsg({
        message: e.detail?.message || "Your session has expired. Please login again.",
        accountId: activeAccountId,
      });
    };

    window.addEventListener("session-expired", handler);
    return () => window.removeEventListener("session-expired", handler);
  }, [activeAccountId, clearActiveAccountState]);

  useEffect(() => {
    if (!user?.id) return;

    socket.emit("register", user.id);

    const onForceLogout = (data) => {
      void clearActiveAccountState();
      setSessionMsg({
        message: data?.message || "Session expired. Logged in from another device.",
        accountId: activeAccountId,
      });
    };

    socket.on("force-logout", onForceLogout);
    return () => socket.off("force-logout", onForceLogout);
  }, [user, activeAccountId, clearActiveAccountState]);

  useEffect(() => {
    const handleSessionExtended = (event) => {
      const sessionExpiresAt = event.detail?.session_expires_at || null;
      if (!user?.id || !sessionExpiresAt) return;

      const result = upsertStoredAccount({
        user,
        session_expires_at: sessionExpiresAt,
      });
      setAccounts(result.accounts);
      setActiveAccountId(result.activeAccountId);
    };

    window.addEventListener("auth-session-extended", handleSessionExtended);
    return () => window.removeEventListener("auth-session-extended", handleSessionExtended);
  }, [user]);

  useEffect(() => {
    if (authHydrating || !user?.id || !activeAccount?.session_expires_at) return undefined;

    const expiresAtMs = new Date(activeAccount.session_expires_at).getTime();
    if (!Number.isFinite(expiresAtMs)) return undefined;

    const logoutAfterMs = Math.max(expiresAtMs - Date.now(), 0);
    const timer = window.setTimeout(() => {
      void clearActiveAccountState();
      setSessionMsg({
        message: "Your session has expired due to inactivity. Please login again.",
        accountId: activeAccountId,
      });
    }, logoutAfterMs);

    return () => window.clearTimeout(timer);
  }, [authHydrating, user?.id, activeAccount?.session_expires_at, activeAccountId, clearActiveAccountState]);

  useEffect(() => {
    setRoleMenuOpen(false);
  }, [activeAccountId, activeRoleId]);

  useEffect(() => {
    let mounted = true;

    const hydrateSession = async () => {
      try {
        const res = await API.get("/auth/me", { skipSessionExpired: true });
        const nextUser = res.data?.user;
        if (!mounted || !nextUser) return;
        const result = upsertStoredAccount({
          user: nextUser,
          session_expires_at: res.data?.session_expires_at || null,
        });
        setAccounts(result.accounts);
        setActiveAccountId(result.activeAccountId);
        resetSessionExpiredGuard();
      } catch (err) {
        if (!mounted) return;
        if (err.response?.status === 401) {
          await clearActiveAccountState();
        } else {
          console.error("Failed to hydrate current session:", err);
        }
      } finally {
        if (mounted) setAuthHydrating(false);
      }
    };

    hydrateSession();

    return () => {
      mounted = false;
    };
  }, [clearActiveAccountState]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!accountMenuRef.current?.contains(event.target)) {
        setAccountMenuOpen(false);
        setRoleMenuOpen(false);
      }
      if (
        !workMenuRef.current?.contains(event.target) &&
        !importExportMenuRef.current?.contains(event.target) &&
        !reportsMenuRef.current?.contains(event.target)
      ) {
        setNavDropdownOpen("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const refreshCurrentUser = useCallback(async () => {
    if (!user?.id) return;
    if (refreshCurrentUserPromiseRef.current) {
      return refreshCurrentUserPromiseRef.current;
    }

    refreshCurrentUserPromiseRef.current = (async () => {
      const res = await API.get("/auth/me");
      const nextUser = res.data?.user;
      if (!nextUser) return;
      const result = updateStoredAccountUser(activeAccountId || nextUser.id, nextUser);
      setAccounts(result.accounts);
      setActiveAccountId(result.activeAccountId);
    })();

    try {
      await refreshCurrentUserPromiseRef.current;
    } catch (err) {
      console.error("Failed to hydrate current user:", err);
    } finally {
      refreshCurrentUserPromiseRef.current = null;
    }
  }, [user?.id, activeAccountId]);

  useEffect(() => {
    if (!user?.id) return undefined;

    const handleAccessRefresh = () => {
      const now = Date.now();
      if (now - lastUserRefreshAtRef.current < 5000) return;
      lastUserRefreshAtRef.current = now;
      refreshCurrentUser();
    };

    const handleVisibilityRefresh = () => {
      if (document.visibilityState === "visible") {
        refreshCurrentUser();
      }
    };

    window.addEventListener("focus", handleAccessRefresh);
    window.addEventListener("auth-refresh-request", handleAccessRefresh);
    document.addEventListener("visibilitychange", handleVisibilityRefresh);

    return () => {
      window.removeEventListener("focus", handleAccessRefresh);
      window.removeEventListener("auth-refresh-request", handleAccessRefresh);
      document.removeEventListener("visibilitychange", handleVisibilityRefresh);
    };
  }, [user?.id, refreshCurrentUser]);

  // ─── Auth handlers ───────────────────────────────────────────
  const handleLogin = (data) => {
    setRouteHistoryMode("replace");
    const result = upsertStoredAccount(data);
    setAccounts(result.accounts);
    setActiveAccountId(result.activeAccountId);
    resetSessionExpiredGuard();
    setSessionMsg(null);
    setAddAccountOpen(false);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
    setMobileOpen(false);
    setView("search");
    setDetailReturnView("search");
    setSelectedProfileId(null);
    setSelectedProfileMode("view");
    setSelectedProfileInitialTab(null);
    setSelectedProfileEditSection(null);
    setSelectedProfileLockedSection(null);
    setSelectedProfileAlertType("all");
    setUserManagementTab("users");
    setUserManagementProfileId(null);
    setCreateBiodataItemId(null);
    setTaskAssignmentTab(null);
    setTaskAssignmentWorkspaceTab(null);
    setImportExportBatchId(null);
  };

  const performLogout = async () => {
    let hasRemainingSessions = false;
    let logoutCompleted = !user?.id;
    try {
      if (user?.id) {
        const response = await API.post("/auth/logout");
        hasRemainingSessions = !!response.data?.has_remaining_sessions;
        logoutCompleted = true;
      }
    } catch (err) {
      console.error("Logout API failed:", err);
    } finally {
      if (!logoutCompleted) {
        setLogoutConfirmOpen(false);
        return;
      }

      setRouteHistoryMode("replace");
      clearAllCreateProfileDraftsForUser(user);
      const result = removeStoredAccount(activeAccountId || activeAccount?.id || user?.id);
      setAccounts(result.accounts);
      setActiveAccountId(result.activeAccountId);
      if (!hasRemainingSessions) {
        const cleared = await clearBrowserStorageData();
        setAccounts(cleared.accounts);
        setActiveAccountId(cleared.activeAccountId);
      }
      setSessionMsg(null);
      setView("search");
      setDetailReturnView("search");
      setSelectedProfileId(null);
      setSelectedProfileMode("view");
      setSelectedProfileInitialTab(null);
      setSelectedProfileEditSection(null);
      setSelectedProfileLockedSection(null);
      setSelectedProfileAlertType("all");
      setUserManagementTab("users");
      setUserManagementProfileId(null);
      setCreateBiodataItemId(null);
      setTaskAssignmentTab(null);
      setTaskAssignmentWorkspaceTab(null);
      setImportExportBatchId(null);
      setAccountMenuOpen(false);
      setLogoutConfirmOpen(false);
      setAddAccountOpen(false);
      setRoleMenuOpen(false);
      window.setTimeout(() => {
        window.location.replace(`${window.location.pathname}${window.location.search}`);
      }, 0);
    }
  };

  const requestLogout = () => {
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
    setLogoutConfirmOpen(true);
  };

  const setRouteHistoryMode = useCallback((mode = "replace") => {
    routeHistoryModeRef.current = mode === "push" ? "push" : "replace";
  }, []);

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((current) => {
      const next = !current;
      if (typeof window !== "undefined") {
        window.localStorage.setItem("jk-sidebar-collapsed", next ? "1" : "0");
      }
      return next;
    });
  }, []);

  const handleViewProfile = (id, options = {}) => {
    if (!canViewProfile) return;
    setRouteHistoryMode("push");
    setDetailReturnView(view === "detail" ? detailReturnView : view);
    setSelectedProfileId(id);
    setSelectedProfileMode(
      typeof options?.mode === "string" && options.mode.trim().toLowerCase() === "edit" ? "edit" : "view"
    );
    setSelectedProfileInitialTab(
      typeof options?.initialTab === "string" && options.initialTab.trim()
        ? options.initialTab.trim()
        : null
    );
    setSelectedProfileEditSection(normalizeProfileSection(options?.editSection));
    setSelectedProfileLockedSection(normalizeProfileSection(options?.lockedSection));
    // Recommendation-overlay navigation: remember where to send the user back
    // to ("Back to Overlay") on the profile we're opening, and/or which
    // overlay to reopen on it if we're navigating back to a previous profile.
    setProfileOverlayReturn(options?.overlayReturn || null);
    setProfileReopenOverlay(options?.reopenOverlay || null);
    setView("detail");
    setMobileOpen(false);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
  };

  const handleProfileTabChange = useCallback((tabId) => {
    const normalizedTab = typeof tabId === "string" && tabId.trim() ? tabId.trim() : "";
    const nextTab = normalizedTab && normalizedTab !== "overview" ? normalizedTab : null;
    setRouteHistoryMode("replace");
    setSelectedProfileInitialTab((current) => (current === nextTab ? current : nextTab));
  }, [setRouteHistoryMode]);

  const handleProfileModeChange = useCallback((mode = "view", section = null) => {
    setRouteHistoryMode("push");
    const nextMode = mode === "edit" ? "edit" : "view";
    setSelectedProfileMode(nextMode);
    setSelectedProfileEditSection(nextMode === "edit" ? section || null : null);
    setSelectedProfileLockedSection(null);
  }, [setRouteHistoryMode]);

  const handleBack = () => {
    setRouteHistoryMode("push");
    setSelectedProfileId(null);
    setSelectedProfileMode("view");
    setSelectedProfileInitialTab(null);
    setSelectedProfileEditSection(null);
    setSelectedProfileLockedSection(null);
    setView(detailReturnView || "search");
    setMobileOpen(false);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
  };

  const handleViewProfileAlerts = (options = {}) => {
    setSelectedProfileAlertType(
      normalizeProfileAlertType(options?.alertType) || "all"
    );
    changeView("profile-alerts");
  };

  const handleProfileAlertTypeChange = useCallback((alertType) => {
    const nextAlertType = normalizeProfileAlertType(alertType) || "all";
    setRouteHistoryMode("replace");
    setSelectedProfileAlertType((current) => (current === nextAlertType ? current : nextAlertType));
  }, [setRouteHistoryMode]);

  const handleUserManagementTabChange = useCallback((tabId) => {
    const nextTab = normalizeUserManagementTab(tabId) || "users";
    setRouteHistoryMode("replace");
    setUserManagementTab((current) => (current === nextTab ? current : nextTab));
    if (nextTab !== "profile_verification") {
      setUserManagementProfileId(null);
    }
  }, [setRouteHistoryMode]);

  const handleUserManagementProfileChange = useCallback((profileId) => {
    const nextProfileId = String(profileId || "").trim() || null;
    setRouteHistoryMode(nextProfileId ? "push" : "replace");
    setUserManagementTab("profile_verification");
    setUserManagementProfileId((current) => (current === nextProfileId ? current : nextProfileId));
  }, [setRouteHistoryMode]);
  const handleOpenPermissionDocs = useCallback(() => {
    setRouteHistoryMode("push");
    setUserManagementTab("features");
    setUserManagementProfileId(null);
    setView("permission-docs");
    setMobileOpen(false);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
  }, [setRouteHistoryMode]);
  const handleClosePermissionDocs = useCallback(() => {
    setRouteHistoryMode("push");
    setUserManagementTab("features");
    setUserManagementProfileId(null);
    setView("users");
    setMobileOpen(false);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
  }, [setRouteHistoryMode]);

  const handleTaskAssignmentRouteChange = useCallback((nextTaskTab = null, nextWorkspaceTab = null) => {
    const normalizedTaskTab = normalizeTaskAssignmentTab(nextTaskTab) || null;
    const normalizedWorkspaceTab = normalizeTaskAssignmentWorkspaceTab(nextWorkspaceTab) || null;
    setRouteHistoryMode("replace");
    setTaskAssignmentTab((current) => (current === normalizedTaskTab ? current : normalizedTaskTab));
    setTaskAssignmentWorkspaceTab((current) => (current === normalizedWorkspaceTab ? current : normalizedWorkspaceTab));
  }, [setRouteHistoryMode]);

  const handleImportExportBatchChange = useCallback((batchId) => {
    const nextBatchId = String(batchId || "").trim() || null;
    setRouteHistoryMode("replace");
    setImportExportBatchId((current) => (current === nextBatchId ? current : nextBatchId));
  }, [setRouteHistoryMode]);

  const handleSessionClose = async () => {
    setRouteHistoryMode("replace");
    await clearActiveAccountState();
    setSessionMsg(null);
    setView("search");
    setDetailReturnView("search");
    setSelectedProfileId(null);
    setSelectedProfileMode("view");
    setSelectedProfileInitialTab(null);
    setSelectedProfileEditSection(null);
    setSelectedProfileLockedSection(null);
    setSelectedProfileAlertType("all");
    setUserManagementTab("users");
    setUserManagementProfileId(null);
    setCreateBiodataItemId(null);
    setTaskAssignmentTab(null);
    setTaskAssignmentWorkspaceTab(null);
    setImportExportBatchId(null);
    setAccountMenuOpen(false);
    setLogoutConfirmOpen(false);
    setAddAccountOpen(false);
    setRoleMenuOpen(false);
  };

  const handleSwitchAccount = async (accountId) => {
    if (!accountId || accountId === activeAccountId) {
      setAccountMenuOpen(false);
      setRoleMenuOpen(false);
      return;
    }

    try {
      const res = await API.post("/auth/switch-account", { user_id: accountId });
      const localSelection = setStoredActiveAccount(accountId);
      const result = upsertStoredAccount(res.data);
      const mergedResult = setStoredActiveAccount(result.activeAccountId || localSelection.activeAccountId);
      setAccounts(mergedResult.accounts);
      setActiveAccountId(mergedResult.activeAccountId);
      resetSessionExpiredGuard();
      setSessionMsg(null);
      setAccountMenuOpen(false);
      setRoleMenuOpen(false);
      setMobileOpen(false);
      setLogoutConfirmOpen(false);
      setAddAccountOpen(false);
      window.location.reload();
    } catch (err) {
      console.error("Account switch failed:", err);
      setSessionMsg({
        message: err.response?.data?.message || err.response?.data?.error || "Failed to switch account.",
        accountId,
      });
    }
  };

  const openAddAccount = () => {
    setAddAccountOpen(true);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
    setMobileOpen(false);
    setLogoutConfirmOpen(false);
  };

  const handleSwitchRole = async (roleId) => {
    const nextRoleId = Number(roleId) || null;

    if (!nextRoleId || nextRoleId === Number(activeRoleId || 0) || roleSwitching) {
      return;
    }

    setRoleSwitching(true);

    try {
      const res = await API.post("/auth/switch-role", { role_id: nextRoleId });
      const result = upsertStoredAccount(res.data);
      setAccounts(result.accounts);
      setActiveAccountId(result.activeAccountId);
      resetSessionExpiredGuard();
      setSessionMsg(null);
      setRouteHistoryMode("replace");
      setView("search");
      setDetailReturnView("search");
      setSelectedProfileId(null);
      setSelectedProfileMode("view");
      setSelectedProfileInitialTab(null);
      setSelectedProfileEditSection(null);
      setSelectedProfileLockedSection(null);
      setSelectedProfileAlertType("all");
      setUserManagementTab("users");
      setUserManagementProfileId(null);
      setCreateBiodataItemId(null);
      setTaskAssignmentTab(null);
      setTaskAssignmentWorkspaceTab(null);
      setImportExportBatchId(null);
      setMobileOpen(false);
      setAccountMenuOpen(false);
      setLogoutConfirmOpen(false);
      setAddAccountOpen(false);
      setRoleMenuOpen(false);
      window.location.reload();
    } catch (err) {
      console.error("Failed to switch role:", err);
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to switch role.";
      window.alert(message);
    } finally {
      setRoleSwitching(false);
    }
  };

  const changeView = (nextView) => {
    setRouteHistoryMode("push");
    setView(nextView);
    if (nextView !== "create") {
      setCreateBiodataItemId(null);
    }
    if (nextView !== "task-assignment") {
      setTaskAssignmentTab(null);
      setTaskAssignmentWorkspaceTab(null);
    }
    if (nextView !== "import-workspace") {
      setImportExportBatchId(null);
    }
    if (nextView !== "users") {
      setUserManagementTab("users");
      setUserManagementProfileId(null);
    }
    if (nextView !== "detail") {
      setDetailReturnView(nextView);
      setSelectedProfileMode("view");
      setSelectedProfileInitialTab(null);
      setSelectedProfileEditSection(null);
      setSelectedProfileLockedSection(null);
    }
    setMobileOpen(false);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
    setNavDropdownOpen("");
  };

  const openAppViewInNewTab = (hash) => {
    if (typeof window === "undefined") return;
    const nextUrl = `${window.location.pathname}${window.location.search}${hash}`;
    window.open(nextUrl, "_blank", "noopener,noreferrer");
  };

  const openBiodataProfileWorkflow = (biodataItemId) => {
    const nextItemId = String(biodataItemId || "").trim();
    if (!nextItemId) return;
    setRouteHistoryMode("push");
    setCreateBiodataItemId(nextItemId);
    setView("create");
    setDetailReturnView("task-assignment");
    setTaskAssignmentTab("assigned-to-me");
    setTaskAssignmentWorkspaceTab("biodata");
    setMobileOpen(false);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
    setNavDropdownOpen("");
  };

  const openTaskAssignment = (taskTab = null, workspaceTab = null) => {
    setRouteHistoryMode("push");
    setTaskAssignmentTab(normalizeTaskAssignmentTab(taskTab) || null);
    setTaskAssignmentWorkspaceTab(normalizeTaskAssignmentWorkspaceTab(workspaceTab) || null);
    setView("task-assignment");
    setDetailReturnView("task-assignment");
    setMobileOpen(false);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
    setNavDropdownOpen("");
  };

  const openImportWorkspace = () => {
    setRouteHistoryMode("push");
    setImportExportBatchId(null);
    setView("import-workspace");
    setDetailReturnView("import-workspace");
    setMobileOpen(false);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
    setNavDropdownOpen("");
  };

  const openExportWorkspace = () => {
    setRouteHistoryMode("push");
    setView("export-workspace");
    setDetailReturnView("export-workspace");
    setMobileOpen(false);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
    setNavDropdownOpen("");
  };

  // ─── Guard: if current view is no longer allowed, reset ──────
  useEffect(() => {
    if (authHydrating) return;
    if (view === "dashboard" && !canDashboard) {
      setRouteHistoryMode("replace");
      if (canSearch) setView("search");
      else if (canCreate) setView("create");
      else if (canFutureProspects) setView("future-prospects");
      else if (canReports) setView("recommendation-dashboard");
      else if (canHrmsAnalytics) setView("hrms-analytics");
      else if (canImport) setView("import-workspace");
      else if (canExport) setView("export-workspace");
      else if (canDuplicateHistory) setView("duplicates");
      else if (canTaskAssignments) setView("task-assignment");
      else if (canAudit) setView("audit");
      else if (canUsers) setView("users");
    }
    if (view === "search" && !canSearch) {
      setRouteHistoryMode("replace");
      if (canDashboard) setView("dashboard");
      else if (canCreate) setView("create");
      else if (canFutureProspects) setView("future-prospects");
      else if (canReports) setView("recommendation-dashboard");
      else if (canHrmsAnalytics) setView("hrms-analytics");
      else if (canImport) setView("import-workspace");
      else if (canExport) setView("export-workspace");
      else if (canDuplicateHistory) setView("duplicates");
      else if (canTaskAssignments) setView("task-assignment");
      else if (canAudit) setView("audit");
      else if (canUsers) setView("users");
    }
    if (view === "create" && !canCreate) {
      setRouteHistoryMode("replace");
      setView("search");
    }
    if (view === "assigned-profiles" && !canSearch) {
      setRouteHistoryMode("replace");
      setView("search");
    }
    if (view === "recommendation-dashboard" && !canReports) {
      setRouteHistoryMode("replace");
      setView("search");
    }
    if (view === "future-prospects" && !canFutureProspects) {
      setRouteHistoryMode("replace");
      setView("search");
    }
    if (view === "hrms-analytics" && !canHrmsAnalytics) {
      setRouteHistoryMode("replace");
      setView("search");
    }
    if (view === "task-assignment" && !canTaskAssignments) {
      setRouteHistoryMode("replace");
      setView("search");
    }
    if (view === "import-workspace" && !canImport) {
      setRouteHistoryMode("replace");
      setView("search");
    }
    if ((view === "export-workspace" || view === "temp-recommendation-import") && !canExport) {
      setRouteHistoryMode("replace");
      setView("search");
    }
    if (view === "watermarked-pdf-redaction" && !canWatermarkRedaction) {
      setRouteHistoryMode("replace");
      setView("search");
    }
    if (view === "audit" && !canAudit) {
      setRouteHistoryMode("replace");
      setView("search");
    }
    if (view === "users" && !canUsers) {
      setRouteHistoryMode("replace");
      setView("search");
    }
    if (view === "duplicates" && !canDuplicateHistory) {
      setRouteHistoryMode("replace");
      setView("search");
    }
    if (view === "detail" && !canViewProfile) {
      setRouteHistoryMode("replace");
      setView(canSearch ? "search" : "create");
      setSelectedProfileMode("view");
    }
    if (view === "my-profile" && !user) {
      setRouteHistoryMode("replace");
      setView("search");
    }
  }, [authHydrating, view, user, canDashboard, canSearch, canViewProfile, canCreate, canFutureProspects, canReports, canImport, canExport, canWatermarkRedaction, canHrmsAnalytics, canTaskAssignments, canAudit, canUsers, canDuplicateHistory, setRouteHistoryMode]);

  const appContent = authHydrating ? (
    <div style={S.app}>
      <div style={{ ...S.pageShell, textAlign: "center", color: "var(--shell-muted)" }}>
        Checking secure session...
      </div>
    </div>
  ) : !isAuthenticated ? (
    <Login onLogin={handleLogin} />
  ) : (
    <div style={S.app}>
      <style>{`
        .jk-nav-btn,
        .jk-user-pill,
        .jk-nav-dropdown-menu button {
          -webkit-tap-highlight-color: transparent !important;
          outline: none !important;
          appearance: none !important;
        }
        .jk-nav-btn:focus,
        .jk-nav-btn:focus-visible,
        .jk-user-pill:focus,
        .jk-user-pill:focus-visible,
        .jk-nav-dropdown-menu button:focus,
        .jk-nav-dropdown-menu button:focus-visible {
          outline: none !important;
          box-shadow: none !important;
        }
        .jk-nav-btn:active,
        .jk-user-pill:active,
        .jk-nav-dropdown-menu button:active {
          filter: none !important;
        }
        .jk-nav-dropdown-menu button {
          transition: background .18s ease, border-color .18s ease;
        }
        .jk-nav-dropdown-menu button:hover {
          background: var(--shell-logo-1) !important;
          border-color: var(--shell-logo-1) !important;
        }
        .jk-nav-dropdown-menu button:hover span {
          color: #fff !important;
        }
        .jk-sidebar-nav-btn {
          -webkit-tap-highlight-color: transparent !important;
          outline: none !important;
        }
        .jk-sidebar-nav-btn:hover:not([data-active="1"]) {
          background: var(--shell-track) !important;
        }
        .jk-sidebar-collapse-btn:hover {
          background: var(--shell-track) !important;
          color: var(--shell-nav-ink) !important;
        }
        .jk-sidebar-nav::-webkit-scrollbar {
          width: 6px;
        }
        .jk-sidebar-nav::-webkit-scrollbar-thumb {
          background: var(--shell-border-strong);
          border-radius: 999px;
        }
        .jk-mobile-topbar {
          display: none;
        }
        .jk-sidebar-backdrop {
          display: none;
        }
        @media (max-width: 980px) {
          .jk-mobile-topbar {
            display: flex !important;
          }
          .jk-sidebar {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            height: 100vh !important;
            width: min(280px, 82vw) !important;
            transform: translateX(-100%);
            transition: transform 0.22s ease;
            z-index: 220 !important;
          }
          .jk-sidebar-open {
            transform: translateX(0);
          }
          .jk-sidebar-backdrop {
            display: block !important;
            position: fixed !important;
            inset: 0 !important;
            background: rgba(20,20,26,0.45) !important;
            z-index: 200 !important;
            backdrop-filter: blur(2px) !important;
          }
          .jk-main-col {
            width: 100% !important;
          }
        }
      `}</style>

      <div
        style={S.mobileTopBar}
        className="jk-mobile-topbar"
      >
        <button
          type="button"
          style={S.mobileMenuBtn}
          className="jk-mobile-menu-btn"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          ☰
        </button>
        <div style={S.mobileBrandRow}>
          <BrandMark size={26} />
          <span style={S.navBrand}>Jain Kalyanam</span>
        </div>
      </div>

      {mobileOpen && (
        <div
          className="jk-sidebar-backdrop"
          style={S.sidebarBackdrop}
          onClick={() => setMobileOpen(false)}
        />
      )}

      <button
        type="button"
        style={S.appThemeToggle}
        className="jk-app-theme-toggle"
        onClick={() => setDarkModeOn((current) => !current)}
        aria-label={darkModeOn ? "Switch to light mode" : "Switch to dark mode"}
        title={darkModeOn ? "Light mode" : "Dark mode"}
      >
        {darkModeOn ? (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
          </svg>
        ) : (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
          </svg>
        )}
      </button>

      <div style={S.shell}>
      {(() => {
        const defaultView = canSearch
          ? "search"
          : canDashboard
            ? "dashboard"
            : canCreate
              ? "create"
              : canFutureProspects
                ? "future-prospects"
                : canReports
                  ? "recommendation-dashboard"
                  : canImport
                    ? "import-workspace"
                    : canExport
                      ? "export-workspace"
                      : canDuplicateHistory
                        ? "duplicates"
                        : canTaskAssignments
                          ? "task-assignment"
                          : "users";

        const coreItems = [];
        if (canDashboard) {
          coreItems.push({ key: "dashboard", label: "Dashboard", icon: "dashboard", active: view === "dashboard", onClick: () => changeView("dashboard") });
        }
        if (canSearch) {
          coreItems.push({ key: "search", label: "Search", icon: "search", active: view === "search", onClick: () => changeView("search") });
        }
        if (canSearch) {
          coreItems.push({ key: "assigned-profiles", label: "Assigned Profiles", icon: "assigned", active: view === "assigned-profiles", onClick: () => changeView("assigned-profiles") });
        }
        if (canCreate) {
          coreItems.push({ key: "create", label: "Create Profile", icon: "create", active: view === "create", onClick: () => changeView("create") });
        }
        if (canViewProfile) {
          coreItems.push({
            key: "edit-profile",
            label: "Edit Profile",
            icon: "edit",
            active: view === "detail" && selectedProfileMode === "edit",
            onClick: () => {
              if (selectedProfileId) {
                handleViewProfile(selectedProfileId, { mode: "edit" });
              } else {
                changeView("search");
              }
            },
          });
        }
        if (canViewProfile) {
          coreItems.push({
            key: "profile-detail",
            label: "Profile Detail",
            icon: "profileDetail",
            active: view === "detail" && selectedProfileMode !== "edit",
            onClick: () => {
              if (selectedProfileId) {
                handleViewProfile(selectedProfileId, { mode: "view" });
              } else {
                changeView("search");
              }
            },
          });
        }
        if (canFutureProspects) {
          coreItems.push({ key: "future-prospects", label: "Future Prospects", icon: "future", active: view === "future-prospects", onClick: () => changeView("future-prospects") });
        }
        if (canViewAlerts) {
          coreItems.push({ key: "profile-alerts", label: "Profile Alerts", icon: "alerts", active: view === "profile-alerts", onClick: () => handleViewProfileAlerts() });
        }

        const adminItems = [];
        if (canUsers) {
          adminItems.push({ key: "users", label: "User Management", icon: "users", active: view === "users", onClick: () => changeView("users") });
        }
        if (canAudit) {
          adminItems.push({ key: "audit", label: "Audit Logs", icon: "audit", active: view === "audit", onClick: () => changeView("audit") });
        }
        if (canReports) {
          adminItems.push({ key: "recommendation-dashboard", label: "Recommendation Dashboard", icon: "reports", active: view === "recommendation-dashboard", onClick: () => changeView("recommendation-dashboard") });
        }
        if (canHrmsAnalytics) {
          adminItems.push({ key: "hrms-analytics", label: "HRMS Analytics", icon: "hrms", active: view === "hrms-analytics", onClick: () => changeView("hrms-analytics") });
        }
        if (canTaskAssignments) {
          adminItems.push({ key: "task-assignment", label: "Task Assignment", icon: "tasks", active: view === "task-assignment", onClick: () => openTaskAssignment() });
        }
        if (canDuplicateHistory) {
          adminItems.push({ key: "duplicates", label: "Duplicate Merge History", icon: "duplicates", active: view === "duplicates", onClick: () => changeView("duplicates") });
        }
        if (canUsers) {
          adminItems.push({ key: "permission-docs", label: "Permission Docs", icon: "permission", active: view === "permission-docs", onClick: () => handleOpenPermissionDocs() });
        }
        if (canImport) {
          adminItems.push({ key: "import-workspace", label: "Import", icon: "importFile", active: view === "import-workspace", onClick: () => openImportWorkspace() });
        }
        if (canExport) {
          adminItems.push({ key: "export-workspace", label: "Export", icon: "exportFile", active: view === "export-workspace", onClick: () => openExportWorkspace() });
          adminItems.push({ key: "temp-recommendation-import", label: "Temp Recommendation Import", icon: "inbox", active: view === "temp-recommendation-import", onClick: () => changeView("temp-recommendation-import") });
        }
        if (canWatermarkRedaction) {
          adminItems.push({ key: "watermarked-pdf-redaction", label: "Watermarked PDF Redaction", icon: "redact", active: view === "watermarked-pdf-redaction", onClick: () => changeView("watermarked-pdf-redaction") });
        }

        return (
          <aside
            style={{ ...S.sidebar, ...(sidebarCollapsed ? S.sidebarCollapsed : {}) }}
            className={`jk-sidebar ${mobileOpen ? "jk-sidebar-open" : ""}`}
          >
            <div style={S.sidebarBrandRow} onClick={() => changeView(defaultView)}>
              <span style={S.sidebarBrandMark}>
                <BrandMark size={30} />
              </span>
              {!sidebarCollapsed && (
                <div>
                  <div style={S.navBrand}>Jain Kalyanam</div>
                  <div style={S.navSub}>MATRIMONY CONSOLE</div>
                </div>
              )}
            </div>

            <button
              type="button"
              className="jk-sidebar-collapse-btn"
              style={S.sidebarCollapseBtn}
              onClick={toggleSidebarCollapsed}
              aria-label={sidebarCollapsed ? "Expand menu" : "Collapse menu"}
            >
              <Icon name={sidebarCollapsed ? "chevronsRight" : "chevronsLeft"} size={14} />
              {!sidebarCollapsed && <span>Collapse menu</span>}
            </button>

            <nav style={S.sidebarNav} className="jk-sidebar-nav">
              {coreItems.length > 0 && (
                <div style={S.sidebarGroup}>
                  {!sidebarCollapsed && (
                    <button
                      type="button"
                      style={S.sidebarSectionLabel}
                      onClick={() => setSidebarSectionsExpanded((current) => ({ ...current, core: !current.core }))}
                    >
                      <span>Core</span>
                      <Icon name={sidebarSectionsExpanded.core ? "chevronUp" : "chevronDown"} size={12} />
                    </button>
                  )}
                  {(sidebarCollapsed || sidebarSectionsExpanded.core) && coreItems.map((item) => (
                    <SidebarNavBtn key={item.key} item={item} collapsed={sidebarCollapsed} />
                  ))}
                </div>
              )}

              {adminItems.length > 0 && (
                <div style={S.sidebarGroup}>
                  {!sidebarCollapsed && (
                    <button
                      type="button"
                      style={S.sidebarSectionLabel}
                      onClick={() => setSidebarSectionsExpanded((current) => ({ ...current, admin: !current.admin }))}
                    >
                      <span>Admin</span>
                      <Icon name={sidebarSectionsExpanded.admin ? "chevronUp" : "chevronDown"} size={12} />
                    </button>
                  )}
                  {(sidebarCollapsed || sidebarSectionsExpanded.admin) && adminItems.map((item) => (
                    <SidebarNavBtn key={item.key} item={item} collapsed={sidebarCollapsed} />
                  ))}
                </div>
              )}
            </nav>

            <div style={S.sidebarFooter} className="jk-account-menu-wrap" ref={accountMenuRef}>
              <button
                type="button"
                style={{
                  ...S.sidebarUserPill,
                  ...(accountMenuOpen ? S.userPillActive : {}),
                  ...(sidebarCollapsed ? S.sidebarUserPillCollapsed : {}),
                }}
                className="jk-user-pill"
                onClick={() =>
                  setAccountMenuOpen((open) => {
                    const nextOpen = !open;
                    if (!nextOpen) setRoleMenuOpen(false);
                    return nextOpen;
                  })
                }
              >
                <div style={S.avatar}>
                  {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
                </div>
                {!sidebarCollapsed && (
                  <div style={S.userMeta}>
                    <div style={S.userName}>
                      {user?.name || user?.email?.split("@")[0] || "User"}
                    </div>
                    <div style={S.roleBadge}>{role}</div>
                    {userTypeLabel && <div style={S.userTypeBadge}>{userTypeLabel}</div>}
                  </div>
                )}
                {!sidebarCollapsed && <span style={S.userCaret}>{accountMenuOpen ? "▾" : "▴"}</span>}
              </button>

              {accountMenuOpen && sidebarCollapsed && (
                <div style={S.sidebarAccountMenuCollapsed} className="jk-account-menu-dropdown">
                  {accounts.map((account) => {
                    const isCurrent = account.id === activeAccountId;
                    return (
                      <button
                        key={account.id}
                        type="button"
                        style={{ ...S.accountMenuIconBtn, ...(isCurrent ? S.accountSwitcherItemActive : {}) }}
                        onClick={() => handleSwitchAccount(account.id)}
                        title={account.user?.name || account.user?.email || "Account"}
                      >
                        <span style={S.accountSwitcherAvatarMini}>
                          {(account.user?.name || account.user?.email || "U").slice(0, 1).toUpperCase()}
                        </span>
                      </button>
                    );
                  })}
                  <button type="button" style={S.accountMenuIconBtn} onClick={openAddAccount} title="Add Account">
                    <Icon name="create" size={16} />
                  </button>
                  <button type="button" style={S.accountMenuIconBtn} onClick={() => changeView("my-profile")} title="My Profile">
                    <Icon name="profileDetail" size={16} />
                  </button>
                  <button
                    type="button"
                    style={{ ...S.accountMenuIconBtn, ...S.accountMenuDanger }}
                    onClick={requestLogout}
                    title="Logout Current Account"
                  >
                    <Icon name="logout" size={16} />
                  </button>
                </div>
              )}

              {accountMenuOpen && !sidebarCollapsed && (
                <div style={S.sidebarAccountMenu} className="jk-account-menu-dropdown">
                  <div style={S.accountMenuSection}>
                    <div style={S.accountMenuSectionTitle}>Signed In Accounts</div>
                    {accounts.map((account) => {
                      const isCurrent = account.id === activeAccountId;
                      const accountRole = getPrimaryRoleName(account.user);
                      const accountTypeLabel =
                        account.user?.user_type_name ||
                        account.user?.user_type?.name ||
                        account.user?.user_type_code ||
                        null;

                      return (
                        <button
                          key={account.id}
                          type="button"
                          style={{
                            ...S.accountSwitcherItem,
                            ...(isCurrent ? S.accountSwitcherItemActive : {}),
                          }}
                          onClick={() => handleSwitchAccount(account.id)}
                        >
                          <div style={S.accountSwitcherAvatar}>
                            {(account.user?.name || account.user?.email || "U").slice(0, 1).toUpperCase()}
                          </div>
                          <div style={S.accountSwitcherMeta}>
                            <span style={S.accountSwitcherName}>
                              {account.user?.name || account.user?.email?.split("@")[0] || "User"}
                            </span>
                            <span style={S.accountSwitcherEmail}>{account.user?.email || "-"}</span>
                            <span style={S.accountSwitcherSub}>
                              {accountRole}
                              {accountTypeLabel ? ` • ${accountTypeLabel}` : ""}
                            </span>
                          </div>
                          {isCurrent && <span style={S.accountSwitcherChip}>Current</span>}
                        </button>
                      );
                    })}
                  </div>
                  {alternateRoles.length > 0 && (
                    <div style={S.accountMenuSection}>
                      <div style={S.accountMenuSectionTitle}>Switch Role</div>
                      <button
                        type="button"
                        style={{
                          ...S.accountRoleDropdownToggle,
                          ...(roleMenuOpen ? S.accountRoleDropdownToggleOpen : {}),
                        }}
                        onClick={() => setRoleMenuOpen((open) => !open)}
                        disabled={roleSwitching}
                      >
                        <div style={S.accountRoleDropdownValueRow}>
                          <span style={S.accountRoleDropdownValue}>
                            {roleSwitching ? "Switching..." : currentRole?.name || role || "Select role"}
                          </span>
                          <span style={S.accountRoleDropdownCaret}>
                            {roleMenuOpen ? "▴" : "▾"}
                          </span>
                        </div>
                      </button>

                      {roleMenuOpen && (
                        <div style={S.accountRoleDropdownMenu}>
                          {alternateRoles.map((switchRole) => (
                            <button
                              key={switchRole.id}
                              type="button"
                              style={S.accountRoleOption}
                              onClick={() => handleSwitchRole(switchRole.id)}
                              disabled={roleSwitching}
                            >
                              <span style={S.accountRoleOptionName}>
                                {switchRole.name || `Role #${switchRole.id}`}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <button type="button" style={S.accountMenuItem} onClick={openAddAccount}>
                    <span style={S.accountMenuItemTitle}>Add Account</span>
                    <span style={S.accountMenuItemSub}>Sign in to another approved account</span>
                  </button>
                  <button type="button" style={S.accountMenuItem} onClick={() => changeView("my-profile")}>
                    <span style={S.accountMenuItemTitle}>My Profile</span>
                    <span style={S.accountMenuItemSub}>View account details</span>
                  </button>
                  <button type="button" style={{ ...S.accountMenuItem, ...S.accountMenuDanger }} onClick={requestLogout}>
                    <span style={S.accountMenuItemTitle}>Logout Current Account</span>
                    <span style={S.accountMenuItemSub}>End only this web session</span>
                  </button>
                </div>
              )}
            </div>
          </aside>
        );
      })()}

      <div style={S.mainCol} className="jk-main-col">
      {/* Views */}
      <Suspense
        fallback={(
          <main style={S.body}>
            <div style={{ minHeight: "55vh", display: "grid", placeItems: "center", color: BRAND.muted, fontWeight: 800 }}>
              Loading workspace...
            </div>
          </main>
        )}
      >
      <main style={S.body}>
        <div style={S.pageShellWide}>
          {view === "dashboard" && canDashboard && (
            <DashboardHome
              user={user}
              onOpenAuditLogs={canAudit ? () => changeView("audit") : null}
              onOpenRecommendationDashboard={canReports ? () => changeView("recommendation-dashboard") : null}
              onViewProfile={canViewProfile ? handleViewProfile : null}
            />
          )}

          {view === "search" && canSearch && (
            <Search
              onViewProfile={handleViewProfile}
              onViewProfileAlerts={handleViewProfileAlerts}
              onViewAssignedProfiles={() => changeView("assigned-profiles")}
              onViewAssignedTasks={() => openTaskAssignment("assigned-to-me")}
              onOpenMergeHistory={canDuplicateHistory ? () => changeView("duplicates") : null}
              mergeHistoryLabel="Duplicate Merge History"
              onOpenCsvImport={canImport ? openImportWorkspace : null}
              onOpenBulkExport={canExport ? openExportWorkspace : null}
              onOpenFutureProspects={canFutureProspects ? () => changeView("future-prospects") : null}
              canViewProfile={canViewProfile}
              user={user}
              showExactResultCount
            />
          )}

          {view === "profile-alerts" && canViewAlerts && (
            <ProfileAlerts
              onBack={() => changeView("search")}
              onViewProfile={handleViewProfile}
              canViewProfile={canViewProfile}
              initialAlertType={selectedProfileAlertType}
              onAlertTypeChange={handleProfileAlertTypeChange}
            />
          )}

          {view === "assigned-profiles" && canSearch && (
            <AssignedProfiles
              onBack={() => changeView("search")}
              onOpenTasksWorkspace={() => openTaskAssignment("assigned-to-me", "tasks")}
              onOpenBiodataWorkspace={() => openTaskAssignment("assigned-to-me", "biodata")}
              onSwitchToAssignedByMe={() => openTaskAssignment(null, "profiles")}
              onViewProfile={handleViewProfile}
              canViewProfile={canViewProfile}
            />
          )}

          {keepCreateMounted && (
            <div style={{ display: view === "create" ? "block" : "none" }}>
              <CreateProfile
                token={token}
                user={user}
                freshStartKey={createProfileFreshStartKey}
                onDone={() => {
                  if (createBiodataItemId) {
                    setCreateBiodataItemId(null);
                    openTaskAssignment("assigned-to-me", "biodata");
                    return;
                  }
                  setView("search");
                  setDetailReturnView("search");
                }}
                onViewProfile={handleViewProfile}
                workflowContext={createBiodataItemId ? { type: "biodata", itemId: createBiodataItemId } : null}
              />
            </div>
          )}

          {view === "detail" && selectedProfileId && canViewProfile && (
            <ProfileDetail
              profileId={selectedProfileId}
              mode={selectedProfileMode}
              initialTab={selectedProfileInitialTab}
              editInitialSection={selectedProfileEditSection}
              editLockedSection={selectedProfileLockedSection}
              token={token}
              onBack={handleBack}
              returnView={detailReturnView}
              onTabChange={handleProfileTabChange}
              onModeChange={handleProfileModeChange}
              onOpenAssignedToMe={() => changeView("assigned-profiles")}
              onOpenAssignedByMe={() => openTaskAssignment(null, "profiles")}
              backLabel={
                detailReturnView === "create"
                  ? "Back to Profile"
                  : detailReturnView === "assigned-profiles"
                    ? "Back to Assigned Profiles"
                    : detailReturnView === "future-prospects"
                      ? "Back to Future Prospects"
                      : detailReturnView === "task-assignment"
                        ? "Back to Task Assignment"
                        : detailReturnView === "recommendation-dashboard"
                          ? "Back to Reports"
                          : detailReturnView === "profile-alerts"
                            ? "Back to Profile Alerts"
                            : detailReturnView === "import-workspace"
                              ? "Back to Import"
                              : detailReturnView === "export-workspace"
                                ? "Back to Export"
                              : detailReturnView === "users"
                                ? "Back to Users"
                                : "Back to Search"
              }
              user={user}
              onViewProfile={handleViewProfile}
              overlayReturnTarget={profileOverlayReturn}
              reopenOverlay={profileReopenOverlay}
            />
          )}

          {view === "recommendation-dashboard" && canReports && (
            <RecommendationDashboard
              token={token}
              user={user}
              canViewProfile={canViewProfile}
              onViewProfile={handleViewProfile}
            />
          )}

          {keepFutureProspectsMounted && (
            <div style={{ display: view === "future-prospects" ? "block" : "none" }}>
              <FutureProspects
                user={user}
                active={view === "future-prospects"}
                onBack={() => changeView("search")}
                onViewProfile={handleViewProfile}
                canViewProfile={canViewProfile}
                canUpdateFutureProspects={canUpdateFutureProspects}
              />
            </div>
          )}

          {view === "hrms-analytics" && canHrmsAnalytics && <HrmsAnalytics user={user} canRefresh={canHrmsAnalyticsRebuild} />}

          {view === "task-assignment" && canTaskAssignments && (
            <TaskAssignment
              token={token}
              user={user}
              onSwitchToAssignedToMe={() => changeView("assigned-profiles")}
              onOpenBiodataWorkflow={openBiodataProfileWorkflow}
              onViewProfile={handleViewProfile}
              forcedTaskTab={taskAssignmentTab}
              forcedWorkspaceTab={taskAssignmentWorkspaceTab}
              onRouteChange={handleTaskAssignmentRouteChange}
            />
          )}

          {((view === "import-workspace" && canImport) || (view === "export-workspace" && canExport)) && (
            <ImportExport
              token={token}
              user={user}
              mode={view === "export-workspace" ? "export" : "import"}
              forcedBatchId={importExportBatchId}
              onBatchChange={handleImportExportBatchChange}
              onViewProfile={handleViewProfile}
            />
          )}

          {view === "temp-recommendation-import" && canExport && (
            <TempRecommendationImport
              token={token}
              user={user}
            />
          )}

          {view === "watermarked-pdf-redaction" && canWatermarkRedaction && (
            <WatermarkedPdfRedactionPage token={token} />
          )}

          {view === "my-profile" && user && <MyProfile user={user} />}

          {view === "audit" && canAudit && <AuditLogs token={token} user={user} />}

          {view === "duplicates" && canDuplicateHistory && (
            <DuplicateMergeHistory token={token} canResolve={canResolveDuplicates} user={user} />
          )}

          {view === "users" && canUsers && (
            <UserManagement
              token={token}
              user={user}
              forcedTab={userManagementTab}
              onTabChange={handleUserManagementTabChange}
              forcedVerificationProfileId={userManagementProfileId}
              onVerificationProfileChange={handleUserManagementProfileChange}
              onOpenPermissionDocs={handleOpenPermissionDocs}
            />
          )}

          {view === "permission-docs" && canUsers && (
            <PermissionDocs
              token={token}
              onBack={handleClosePermissionDocs}
            />
          )}

          {view === "yashfe" && <YashFe />}

          {view !== "yashfe" && !canDashboard && !canSearch && !canCreate && !canFutureProspects && !canReports && !canImport && !canExport && !canTaskAssignments && !canAudit && !canDuplicateHistory && !canUsers && (
            <div style={S.emptyState}>
              <h2 style={S.emptyTitle}>No Access Configured</h2>
              <p style={S.emptyText}>
                Your account is active{user?.user_type_name ? ` under ${user.user_type_name}` : ""}, but no frontend-accessible permissions are currently assigned. Please ask an administrator to map features and policies to your role.
              </p>
            </div>
          )}
        </div>
      </main>
      </Suspense>
      </div>
      </div>

      {/* Session Expired Modal */}
      {sessionMsg && (
        <div style={S.modalBackdrop}>
          <div
            ref={sessionExpiredModalTrap.containerRef}
            tabIndex={-1}
            onKeyDown={sessionExpiredModalTrap.onKeyDown}
            style={{ ...S.modal, outline: "none" }}
          >
            <div style={S.modalIcon}>!</div>
            <h3 style={S.modalTitle}>Session Expired</h3>
            <p style={S.modalText}>{sessionMsg?.message || sessionMsg}</p>
            <button style={S.modalBtn} onClick={handleSessionClose}>
              Continue
            </button>
          </div>
        </div>
      )}

      {addAccountOpen && (
        <div style={S.modalBackdrop}>
          <div
            ref={addAccountModalTrap.containerRef}
            tabIndex={-1}
            onKeyDown={addAccountModalTrap.onKeyDown}
            style={{ ...S.accountAddModal, outline: "none" }}
          >
            <div style={S.accountAddModalHeader}>
              <div>
                <h3 style={S.modalTitle}>Add Another Account</h3>
                <p style={S.accountAddModalText}>
                  Sign in with another approved account. Your current account will stay available in this browser.
                </p>
              </div>
              <button
                type="button"
                style={S.accountAddCloseBtn}
                onClick={() => setAddAccountOpen(false)}
                aria-label="Close add account"
              >
                ×
              </button>
            </div>
            <Login
              embedded
              onLogin={handleLogin}
              onCancel={() => setAddAccountOpen(false)}
              showHeader={false}
              submitLabel="Add Account"
              eyebrow="Add another account"
              title="Sign in without logging out"
              subtitle="We’ll keep your current session and add the new account to the switcher."
            />
          </div>
        </div>
      )}

      {logoutConfirmOpen && (
        <div style={S.modalBackdrop}>
          <div
            ref={logoutConfirmModalTrap.containerRef}
            tabIndex={-1}
            onKeyDown={logoutConfirmModalTrap.onKeyDown}
            style={{ ...S.modal, outline: "none" }}
          >
            <div style={S.modalIcon}>?</div>
            <h3 style={S.modalTitle}>Confirm Logout</h3>
            <p style={S.modalText}>Are you sure you want to log out from this web session?</p>
            <div style={S.modalActions}>
              <button style={S.modalSecondaryBtn} onClick={() => setLogoutConfirmOpen(false)}>
                Cancel
              </button>
              <button style={S.modalDangerBtn} onClick={performLogout}>
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{appContent}</GoogleOAuthProvider>;
}

function BrandMark({ size = 30 }) {
  // Matches Login.js's RingsIcon exactly (same viewBox, colors, geometry) so the mark is
  // identical between the login screen and the sidebar. `size` is the rendered width; height
  // follows the icon's native 36:24 aspect ratio.
  const height = Math.round((size * 24) / 36);
  return (
    <svg width={size} height={height} viewBox="0 0 36 24" fill="none" aria-hidden="true">
      <circle cx="14" cy="12" r="10" fill="none" stroke="#e0344a" strokeWidth="2.6" />
      <circle cx="22" cy="12" r="10" fill="none" stroke="#3b6fe0" strokeWidth="2.6" />
    </svg>
  );
}

function Icon({ name, size = 18 }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };
  switch (name) {
    case "dashboard":
      return (
        <svg {...common}>
          <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
          <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
          <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
          <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
        </svg>
      );
    case "search":
      return (
        <svg {...common}>
          <circle cx="10.5" cy="10.5" r="6.5" />
          <line x1="20" y1="20" x2="15.3" y2="15.3" />
        </svg>
      );
    case "assigned":
      return (
        <svg {...common}>
          <rect x="5" y="4" width="14" height="17" rx="2" />
          <path d="M9 3.5h6a1 1 0 011 1V6H8V4.5a1 1 0 011-1z" />
          <path d="M9 12.5l2 2 4-4.5" />
        </svg>
      );
    case "create":
      return (
        <svg {...common}>
          <circle cx="9" cy="8" r="3.5" />
          <path d="M2.5 20.5a6.5 6.5 0 0113 0" />
          <path d="M18 8v5M15.5 10.5h5" />
        </svg>
      );
    case "profileDetail":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="10.2" r="3.2" />
          <path d="M6.7 18.2a5.5 5.5 0 0110.6 0" />
        </svg>
      );
    case "edit":
      return (
        <svg {...common}>
          <path d="M4 20h4l10-10-4-4L4 16v4Z" strokeLinejoin="round" />
          <path d="M12.5 6.5l4 4" strokeLinecap="round" />
        </svg>
      );
    case "future":
      return (
        <svg {...common}>
          <path d="M6 3h12a1 1 0 011 1v16.2a.6.6 0 01-.93.5L12 16.8l-6.07 3.9A.6.6 0 015 20.2V4a1 1 0 011-1z" />
        </svg>
      );
    case "alerts":
      return (
        <svg {...common}>
          <path d="M12 3.2a5.3 5.3 0 00-5.3 5.3v2.7c0 1.4-.7 3-1.9 4.3h14.4c-1.2-1.3-1.9-2.9-1.9-4.3V8.5A5.3 5.3 0 0012 3.2z" />
          <path d="M9.6 19.5a2.4 2.4 0 004.8 0" />
        </svg>
      );
    case "users":
      return (
        <svg {...common}>
          <circle cx="9" cy="8" r="3.3" />
          <circle cx="17" cy="9.2" r="2.7" />
          <path d="M2.8 20.2a6.2 6.2 0 0112.4 0" />
          <path d="M14.5 14.8a5.4 5.4 0 016.2 4.9" />
        </svg>
      );
    case "audit":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8.5" />
          <path d="M12 7.5V12l3 2.3" />
        </svg>
      );
    case "reports":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="12" cy="12" r="0.6" fill="currentColor" />
        </svg>
      );
    case "hrms":
      return (
        <svg {...common}>
          <line x1="5.5" y1="20" x2="5.5" y2="12.5" />
          <line x1="12" y1="20" x2="12" y2="5.5" />
          <line x1="18.5" y1="20" x2="18.5" y2="15" />
        </svg>
      );
    case "tasks":
      return (
        <svg {...common}>
          <rect x="3.5" y="3.5" width="17" height="17" rx="3" />
          <path d="M8 12.3l2.6 2.6L16.5 9" />
        </svg>
      );
    case "duplicates":
      return (
        <svg {...common}>
          <circle cx="6.5" cy="6" r="2" />
          <circle cx="6.5" cy="18" r="2" />
          <circle cx="17.5" cy="12" r="2" />
          <path d="M6.5 8v8" />
          <path d="M6.5 15.5c0-3.6 3.6-5.3 9-5.8" />
        </svg>
      );
    case "permission":
      return (
        <svg {...common}>
          <path d="M12 3.3l6.5 2.6v5.4c0 4.5-3 6.9-6.5 8.4-3.5-1.5-6.5-3.9-6.5-8.4V5.9L12 3.3z" />
          <path d="M9 12.3l2 2 4-4.3" />
        </svg>
      );
    case "importFile":
      return (
        <svg {...common}>
          <path d="M12 3v11" />
          <path d="M7.5 10l4.5 4.5L16.5 10" />
          <path d="M5 20.5h14" />
        </svg>
      );
    case "exportFile":
      return (
        <svg {...common}>
          <path d="M12 20.5v-11" />
          <path d="M7.5 13.5L12 9l4.5 4.5" />
          <path d="M5 3.5h14" />
        </svg>
      );
    case "importExport":
      return (
        <svg {...common}>
          <path d="M8 4v13" />
          <path d="M5 14l3 3 3-3" />
          <path d="M16 20V7" />
          <path d="M19 10l-3-3-3 3" />
        </svg>
      );
    case "redact":
      return (
        <svg {...common}>
          <path d="M6 3.5h9l3 3v14h-12z" />
          <path d="M15 3.5v3h3" />
          <path d="M8.5 13.5h7" strokeWidth="3.5" />
          <path d="M8.5 17h4" />
        </svg>
      );
    case "inbox":
      return (
        <svg {...common}>
          <path d="M3.3 12h4.4l1.8 2.6h4.9l1.8-2.6h4.4" />
          <path d="M4.5 12L6 5.3a1 1 0 011-.8h10a1 1 0 011 .8L19.5 12" />
          <path d="M4 12v6.5a1 1 0 001 1h14a1 1 0 001-1V12" />
        </svg>
      );
    case "chevronLeft":
      return (
        <svg {...common}>
          <polyline points="15 5 8 12 15 19" />
        </svg>
      );
    case "chevronRight":
      return (
        <svg {...common}>
          <polyline points="9 5 16 12 9 19" />
        </svg>
      );
    case "chevronUp":
      return (
        <svg {...common}>
          <polyline points="5 15 12 8 19 15" />
        </svg>
      );
    case "chevronDown":
      return (
        <svg {...common}>
          <polyline points="5 9 12 16 19 9" />
        </svg>
      );
    case "chevronsLeft":
      return (
        <svg {...common}>
          <polyline points="17 5 10 12 17 19" />
          <polyline points="11 5 4 12 11 19" />
        </svg>
      );
    case "chevronsRight":
      return (
        <svg {...common}>
          <polyline points="7 5 14 12 7 19" />
          <polyline points="13 5 20 12 13 19" />
        </svg>
      );
    case "logout":
      return (
        <svg {...common}>
          <path d="M13 4H6a1 1 0 00-1 1v14a1 1 0 001 1h7" />
          <path d="M10 12h10M17 8l3.5 4-3.5 4" />
        </svg>
      );
    default:
      return null;
  }
}

function SidebarNavBtn({ item, collapsed }) {
  return (
    <button
      type="button"
      className="jk-sidebar-nav-btn"
      data-active={item.active ? "1" : "0"}
      style={{
        ...S.sidebarNavBtn,
        ...(item.active ? S.sidebarNavBtnActive : {}),
        ...(collapsed ? S.sidebarNavBtnCollapsed : {}),
      }}
      onClick={item.onClick}
      title={collapsed ? item.label : undefined}
    >
      <span style={S.sidebarNavIcon}>
        <Icon name={item.icon} size={16} />
      </span>
      {!collapsed && <span style={S.sidebarNavLabel}>{item.label}</span>}
    </button>
  );
}

const S = {
  app: {
    minHeight: "100vh",
    background: "var(--shell-surface)",
    fontFamily: "var(--jm-font-body)",
    color: BRAND.text,
  },

  nav: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    padding: "7px 18px",
    backdropFilter: "blur(22px)",
    background: "var(--shell-nav-bg)",
    borderBottom: `1px solid ${BRAND.border}`,
    boxShadow: "0 2px 6px rgba(17,17,20,0.05)",
  },

  navInner: {
    maxWidth: 1480,
    margin: "0 auto",
    minHeight: 46,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },

  navLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    minWidth: 210,
    padding: "4px 6px",
    borderRadius: 16,
    transition: "all 0.18s ease",
  },

  logoMark: {
    width: 34,
    height: 34,
    borderRadius: 11,
    display: "grid",
    placeItems: "center",
    color: "#fff",
    fontWeight: 950,
    fontSize: 13,
    letterSpacing: 0.2,
    background: LOGO_GRADIENT_3,
    boxShadow: "0 16px 36px rgba(185,52,58,0.28)",
  },

  navBrand: {
    fontFamily: "var(--jm-font-heading)",
    fontSize: 15,
    fontWeight: 700,
    color: "var(--shell-accent-text)",
    lineHeight: 1.1,
    letterSpacing: -0.2,
  },

  navSub: {
    marginTop: 2,
    fontSize: 9.5,
    color: BRAND.muted,
    fontWeight: 700,
  },

  navRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  navRightOpen: {},

  mobileMenuBtn: {
    display: "none",
    border: `1px solid ${BRAND.borderStrong}`,
    background: "var(--shell-popover-bg-95)",
    borderRadius: 14,
    padding: "9px 12px",
    fontSize: 18,
    cursor: "pointer",
    color: "var(--shell-accent-text)",
    boxShadow: "0 10px 24px rgba(185,52,58,0.08)",
  },

  userPill: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "4px 10px 4px 4px",
    borderRadius: 12,
    background: BRAND.whiteGlass,
    border: `1px solid ${BRAND.border}`,
    boxShadow: "0 12px 30px rgba(185,52,58,0.08)",
    cursor: "pointer",
    textAlign: "left",
    outline: "none",
    appearance: "none",
    WebkitTapHighlightColor: "transparent",
  },
  userPillActive: {
    borderColor: BRAND.borderStrong,
    background: "var(--shell-popover-bg-98)",
    boxShadow: "0 18px 36px rgba(185,52,58,0.14)",
  },

  avatar: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    color: "#fff",
    fontWeight: 950,
    fontSize: 12,
    background: LOGO_GRADIENT_2,
    boxShadow: "0 10px 24px rgba(185,52,58,0.24)",
  },

  userMeta: {
    lineHeight: 1.1,
    flex: 1,
  },

  userName: {
    fontSize: 10.5,
    color: "var(--shell-pill-ink)",
    fontWeight: 650,
    lineHeight: 1.5,
    maxWidth: 132,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  roleBadge: {
    marginTop: 2,
    fontSize: 9.1,
    color: "var(--shell-ink-faint)",
    fontWeight: 900,
    lineHeight: 1.5,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  userTypeBadge: {
    marginTop: 3,
    fontSize: 9,
    color: BRAND.muted,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  userCaret: {
    color: "var(--shell-accent-text)",
    fontSize: 11,
    fontWeight: 900,
    marginLeft: 2,
  },

  shell: {
    display: "flex",
    alignItems: "stretch",
    minHeight: "100vh",
  },

  appThemeToggle: {
    position: "fixed",
    top: 16,
    right: 44,
    zIndex: 210,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 30,
    height: 30,
    flexShrink: 0,
    border: `1px solid ${BRAND.border}`,
    borderRadius: 999,
    background: "var(--shell-surface)",
    color: BRAND.muted,
    lineHeight: 1,
    cursor: "pointer",
  },

  mobileTopBar: {
    position: "sticky",
    top: 0,
    zIndex: 130,
    alignItems: "center",
    gap: 12,
    padding: "10px 16px",
    background: "var(--shell-topbar-bg)",
    borderBottom: `1px solid ${BRAND.border}`,
    backdropFilter: "blur(18px)",
  },

  mobileBrandRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  sidebarBackdrop: {},

  sidebar: {
    position: "sticky",
    top: 0,
    height: "100vh",
    width: 231,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    background: "var(--shell-surface)",
    borderRight: `1px solid ${BRAND.border}`,
    boxShadow: "2px 0 16px rgba(17,17,20,0.04)",
    transition: "width 0.18s ease",
    overflow: "hidden",
    zIndex: 90,
  },

  sidebarCollapsed: {
    width: 80,
  },

  sidebarBrandRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "22px 20px 16px",
    cursor: "pointer",
  },

  sidebarBrandMark: {
    display: "inline-flex",
    flexShrink: 0,
  },

  sidebarCollapseBtn: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    margin: "0 14px 14px",
    padding: "6px 9px",
    border: `1px solid ${BRAND.border}`,
    borderRadius: 9,
    background: "transparent",
    color: BRAND.muted,
    fontSize: 10.5,
    fontWeight: 600,
    cursor: "pointer",
    outline: "none",
    justifyContent: "flex-start",
  },

  sidebarNav: {
    flex: 1,
    overflowY: "auto",
    padding: "4px 14px 14px",
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },

  sidebarGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },

  sidebarSectionLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: "4px 9px 4px",
    border: "none",
    background: "transparent",
    fontSize: 8.5,
    fontWeight: 700,
    letterSpacing: 0.9,
    textTransform: "uppercase",
    color: BRAND.muted,
    cursor: "pointer",
  },

  sidebarNavBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6.5px 9px",
    borderRadius: 8,
    border: "1px solid transparent",
    background: "transparent",
    color: "var(--shell-nav-ink)",
    fontSize: 10.92,
    fontWeight: 600,
    lineHeight: 1.5,
    textAlign: "left",
    cursor: "pointer",
    outline: "none",
    whiteSpace: "nowrap",
    transition: "background 0.15s ease, color 0.15s ease",
  },

  sidebarNavBtnActive: {
    background: "var(--shell-active-bg)",
    color: "var(--shell-active-fg)",
    fontWeight: 600,
    borderRadius: 7,
    padding: "6.16px 7.7px",
  },

  sidebarNavBtnCollapsed: {
    justifyContent: "center",
    padding: "8px 0",
  },

  sidebarNavIcon: {
    display: "inline-flex",
    flexShrink: 0,
    opacity: 0.8,
  },

  sidebarNavLabel: {
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  sidebarFooter: {
    position: "relative",
  },

  sidebarUserPill: {
    display: "flex",
    alignItems: "center",
    gap: 8.4,
    width: "100%",
    padding: "9.8px 12.6px",
    borderRadius: 0,
    background: "transparent",
    border: "none",
    borderTop: "1px solid var(--shell-pill-border)",
    color: "var(--shell-pill-ink)",
    fontSize: 14,
    lineHeight: "21px",
    cursor: "pointer",
    textAlign: "left",
    outline: "none",
    appearance: "none",
    boxSizing: "border-box",
    WebkitTapHighlightColor: "transparent",
  },

  sidebarUserPillCollapsed: {
    justifyContent: "center",
    padding: "6px",
  },

  sidebarAccountMenu: {
    position: "absolute",
    bottom: "calc(100% + 10px)",
    left: 14,
    right: 14,
    padding: 8,
    borderRadius: 18,
    background: "var(--shell-popover-bg-98)",
    border: `1px solid ${BRAND.borderStrong}`,
    boxShadow: "0 24px 70px rgba(185,52,58,0.18)",
    display: "grid",
    gap: 6,
    zIndex: 120,
    maxHeight: "70vh",
    overflowY: "auto",
    boxSizing: "border-box",
  },

  sidebarAccountMenuCollapsed: {
    position: "absolute",
    bottom: "calc(100% + 10px)",
    left: 8,
    right: 8,
    padding: 6,
    borderRadius: 16,
    background: "var(--shell-popover-bg-98)",
    border: `1px solid ${BRAND.borderStrong}`,
    boxShadow: "0 24px 70px rgba(185,52,58,0.18)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
    zIndex: 120,
  },
  accountMenuIconBtn: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    border: "none",
    background: "var(--shell-red-soft)",
    color: BRAND.text,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },
  accountSwitcherAvatarMini: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    color: "#fff",
    fontWeight: 950,
    fontSize: 10,
    background: LOGO_GRADIENT_2,
  },

  mainCol: {
    flex: 1,
    minWidth: 0,
  },

  navDivider: {
    width: 1,
    height: 30,
    background: BRAND.borderStrong,
    margin: "0 5px",
  },

  navBtn: {
    padding: "7px 12px",
    background: "transparent",
    border: "1px solid transparent",
    borderRadius: 10,
    color: BRAND.text,
    cursor: "pointer",
    fontWeight: 400,
    fontSize: 12,
    transition: "all 0.18s ease",
    whiteSpace: "nowrap",
    outline: "none",
    appearance: "none",
    WebkitTapHighlightColor: "transparent",
  },

  navBtnActive: {
    background: LOGO_GRADIENT_2,
    color: "#fff",
    boxShadow: "0 14px 30px rgba(185,52,58,0.24)",
    border: "1px solid rgba(255,255,255,0.22)",
  },
  navDropdownWrap: {
    position: "relative",
  },
  navDropdownToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "var(--shell-card-bg)",
    border: "none",
  },
  navDropdownToggleOpen: {
    border: "none",
    background: "var(--shell-card-bg)",
    color: BRAND.text,
    boxShadow: "0 16px 34px rgba(185,52,58,0.16)",
  },
  navDropdownCaret: {
    fontSize: 11,
    fontWeight: 900,
    lineHeight: 1,
  },
  navDropdownMenu: {
    position: "absolute",
    top: "calc(100% + 10px)",
    left: 0,
    minWidth: 250,
    padding: 10,
    borderRadius: 14,
    background: "var(--shell-popover-bg-98)",
    border: `1px solid ${BRAND.borderStrong}`,
    boxShadow: "0 24px 70px rgba(185,52,58,0.18)",
    display: "grid",
    gap: 8,
    zIndex: 20,
  },
  navDropdownItem: {
    border: "1px solid rgba(185,52,58,0.10)",
    background: "var(--shell-card-bg)",
    borderRadius: 12,
    padding: "12px 14px",
    textAlign: "left",
    cursor: "pointer",
    display: "grid",
    gap: 4,
    color: BRAND.text,
  },
  navDropdownItemActive: {
    background: "var(--shell-active-item-bg-2)",
    borderColor: BRAND.borderStrong,
    boxShadow: "0 12px 30px rgba(185,52,58,0.10)",
  },
  navDropdownItemTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "var(--shell-ink)",
  },
  navDropdownItemSub: {
    fontSize: 12,
    color: BRAND.muted,
    fontWeight: 500,
    lineHeight: 1.45,
  },

  accountMenuWrap: {
    position: "relative",
    marginLeft: 8,
  },
  accountMenu: {
    position: "absolute",
    top: "calc(100% + 10px)",
    right: 0,
    minWidth: 240,
    padding: 10,
    borderRadius: 22,
    background: "var(--shell-popover-bg-98)",
    border: `1px solid ${BRAND.borderStrong}`,
    boxShadow: "0 24px 70px rgba(185,52,58,0.18)",
    display: "grid",
    gap: 8,
    zIndex: 20,
  },
  accountMenuSection: {
    display: "grid",
    gap: 8,
  },
  accountMenuSectionTitle: {
    padding: "3px 4px 0",
    fontSize: 9.5,
    fontWeight: 900,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: "var(--shell-accent-text)",
  },
  accountMenuItem: {
    border: "none",
    background: "var(--shell-red-soft)",
    borderRadius: 12,
    padding: "9px 10px",
    textAlign: "left",
    cursor: "pointer",
    display: "grid",
    gap: 2,
    minWidth: 0,
    color: BRAND.text,
  },
  accountMenuDanger: {
    background: "var(--shell-red-soft)",
    color: "var(--shell-accent-text)",
  },
  accountRoleDropdownToggle: {
    border: `1px solid ${BRAND.borderStrong}`,
    background: "var(--shell-red-soft)",
    borderRadius: 14,
    padding: "10px 12px",
    textAlign: "left",
    cursor: "pointer",
    display: "block",
    color: BRAND.text,
  },
  accountRoleDropdownToggleOpen: {
    background: "var(--shell-red-soft)",
    boxShadow: "0 12px 26px rgba(185,52,58,0.10)",
  },
  accountRoleDropdownValueRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    flex: 1,
  },
  accountRoleDropdownValue: {
    fontSize: 14,
    fontWeight: 900,
    color: BRAND.text,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  accountRoleDropdownCaret: {
    color: "var(--shell-accent-text)",
    fontSize: 12,
    fontWeight: 900,
    flexShrink: 0,
  },
  accountRoleDropdownMenu: {
    display: "grid",
    gap: 6,
    marginTop: 6,
  },
  accountRoleOption: {
    border: "1px solid rgba(185,52,58,0.12)",
    background: "var(--shell-card-bg)",
    borderRadius: 12,
    padding: "9px 12px",
    textAlign: "left",
    cursor: "pointer",
    display: "block",
    color: BRAND.text,
  },
  accountRoleOptionName: {
    fontSize: 13,
    fontWeight: 900,
    color: BRAND.text,
  },
  accountMenuRoleItemActive: {
    background: "var(--shell-active-item-bg-1)",
    border: `1px solid ${BRAND.borderStrong}`,
    boxShadow: "0 12px 26px rgba(185,52,58,0.10)",
  },
  accountMenuItemTitle: {
    fontSize: 11.5,
    fontWeight: 900,
  },
  accountMenuItemSub: {
    fontSize: 10,
    color: BRAND.muted,
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  accountSwitcherItem: {
    border: "1px solid rgba(185,52,58,0.10)",
    background: "var(--shell-card-bg)",
    borderRadius: 14,
    padding: "8px 9px",
    display: "grid",
    gridTemplateColumns: "30px 1fr auto",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    textAlign: "left",
    minWidth: 0,
  },
  accountSwitcherItemActive: {
    background: "var(--shell-active-item-bg-2)",
    borderColor: BRAND.borderStrong,
    boxShadow: "0 12px 30px rgba(185,52,58,0.10)",
  },
  accountSwitcherAvatar: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    color: "#fff",
    fontWeight: 950,
    fontSize: 12,
    background: LOGO_GRADIENT_2,
  },
  accountSwitcherMeta: {
    minWidth: 0,
    display: "grid",
    gap: 1,
  },
  accountSwitcherName: {
    fontSize: 11,
    fontWeight: 900,
    color: BRAND.text,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  accountSwitcherEmail: {
    fontSize: 9.5,
    color: BRAND.muted,
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  accountSwitcherSub: {
    fontSize: 9.5,
    color: "var(--shell-accent-text)",
    fontWeight: 800,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  accountSwitcherChip: {
    fontSize: 8.5,
    fontWeight: 900,
    color: "var(--shell-accent-text)",
    background: "var(--shell-card-bg)",
    border: `1px solid ${BRAND.borderStrong}`,
    borderRadius: 999,
    padding: "3px 6px",
    whiteSpace: "nowrap",
  },

  body: {
    minHeight: "100vh",
    padding: "0 28px 46px",
    scrollbarGutter: "stable",
    background: "var(--shell-page-bg)",
  },

  pageShell: {
    maxWidth: 1480,
    margin: "0 auto",
  },

  pageShellWide: {
    maxWidth: "100%",
    margin: 0,
  },

  emptyState: {
    background: "var(--shell-card-bg)",
    border: `1px solid ${BRAND.border}`,
    borderRadius: 28,
    padding: 36,
    textAlign: "center",
    boxShadow: "0 18px 54px rgba(185,52,58,0.08)",
  },
  emptyTitle: {
    margin: "0 0 8px",
    color: "var(--shell-accent-text)",
    fontWeight: 700,
    letterSpacing: -0.5,
    lineHeight: 1.12,
  },
  emptyText: {
    margin: 0,
    color: BRAND.muted,
    lineHeight: 1.6,
  },

  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(37,41,53,0.62)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999,
    padding: 20,
    backdropFilter: "blur(10px)",
  },

  modal: {
    background: "var(--shell-card-bg)",
    padding: 30,
    borderRadius: 28,
    textAlign: "center",
    width: "min(390px, 100%)",
    boxShadow: "0 30px 90px rgba(37,41,53,0.30)",
    border: `1px solid ${BRAND.borderStrong}`,
  },

  modalIcon: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    margin: "0 auto 14px",
    background: BRAND.redSoft,
    color: "var(--shell-accent-text)",
    fontWeight: 950,
    fontSize: 24,
  },

  modalTitle: {
    margin: "0 0 8px",
    color: BRAND.text,
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: -0.45,
    lineHeight: 1.12,
  },

  modalText: {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.6,
    color: BRAND.muted,
  },
  accountAddModal: {
    width: "min(520px, calc(100vw - 32px))",
    maxHeight: "calc(100vh - 48px)",
    overflowY: "auto",
    background: "var(--shell-popover-bg-98)",
    borderRadius: 28,
    padding: 22,
    boxShadow: "0 28px 80px rgba(37,41,53,0.22)",
    border: `1px solid ${BRAND.borderStrong}`,
  },
  accountAddModalHeader: {
    marginBottom: 18,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
  },
  accountAddModalText: {
    margin: "8px 0 0",
    color: BRAND.muted,
    fontSize: 14,
    lineHeight: 1.6,
    fontWeight: 600,
  },
  accountAddCloseBtn: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: `1px solid ${BRAND.border}`,
    background: "var(--shell-red-soft)",
    color: "var(--shell-accent-text)",
    fontSize: 22,
    lineHeight: 1,
    fontWeight: 700,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
  },

  modalBtn: {
    marginTop: 20,
    padding: "12px 18px",
    background: LOGO_GRADIENT_2,
    color: "#fff",
    border: "none",
    borderRadius: 999,
    cursor: "pointer",
    fontWeight: 950,
    width: "100%",
    boxShadow: "0 16px 34px rgba(185,52,58,0.24)",
  },
  modalActions: {
    marginTop: 20,
    display: "flex",
    gap: 10,
  },
  modalSecondaryBtn: {
    flex: 1,
    padding: "12px 18px",
    background: "var(--shell-card-bg)",
    color: BRAND.text,
    border: `1px solid ${BRAND.borderStrong}`,
    borderRadius: 999,
    cursor: "pointer",
    fontWeight: 900,
  },
  modalDangerBtn: {
    flex: 1,
    padding: "12px 18px",
    background: LOGO_GRADIENT_2,
    color: "#fff",
    border: "none",
    borderRadius: 999,
    cursor: "pointer",
    fontWeight: 950,
    boxShadow: "0 16px 34px rgba(185,52,58,0.24)",
  },
};
